import React, { useEffect, useRef, useState } from 'react';

const Timeline = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const timelineRef = useRef(null);
  const itemRefs = useRef([]);
  const [progressHeight, setProgressHeight] = useState(0);

  const restaurants = [
    {
      year: '2020',
      name: 'CAFÉ HONS Hazratganj',
      description: 'Our flagship location in the heart of Hazratganj, where traditional charm meets contemporary dining. A celebration of local flavors and warm hospitality that has become a beloved gathering spot for the community.',
      instagram: 'https://instagram.com/cafehons',
      image: 'images/PHOTO-2025-10-28-14-37-58.jpg',
    },
    {
      year: '2021',
      name: 'CAFÉ HONS Gomti Nagar',
      description: 'Expanding our vision to Gomti Nagar, this location brings the same dedication to quality and service in a modern, vibrant setting. A space designed for both intimate meals and lively celebrations.',
      instagram: 'https://instagram.com/cafehons',
      image: '/images/PHOTO-2025-10-28-14-45-19.jpg',
    },
    {
      year: '2022',
      name: 'FO\'SHO',
      description: 'A bold new concept that reimagines Pan-Asian cuisine with an Indian soul. FOSHO represents our commitment to innovation, offering adventurous flavors in a stylish, energetic atmosphere.',
      instagram: 'https://instagram.com/foshoindia',
      image: 'images/entrance.jpg',
    },
    {
      year: '2023',
      name: 'CAFÉ HONS MB Club',
      description: 'Our premium offering at MB Club, designed for discerning palates. An elevated dining experience that combines exclusivity with the welcoming spirit that defines all our establishments.',
      instagram: 'https://instagram.com/cafehons',
      image: 'images/PHOTO-2025-10-28-14-43-29.jpg',
    },
    {
      year: 'Upcoming Project',
      name: 'Otoño',
      description: 'Our latest venture celebrates seasonal ingredients and artisanal techniques. Otono embodies our evolving vision of New Age Indian Hospitality, where every dish tells a story of the season.',
      instagram: 'https://instagram.com/otono',
      image: '/images/PHOTO-2025-10-28-14-52-52.jpg',
    },
  ];

  // Reveal items on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = itemRefs.current.indexOf(entry.target);
          if (entry.isIntersecting && !visibleItems.includes(index)) {
            setVisibleItems((prev) => [...prev, index]);
          }
        });
      },
      { threshold: 0.3 }
    );
  
    const refs = itemRefs.current; // ✅ FIX: store stable reference
  
    refs.forEach((ref) => ref && observer.observe(ref));
  
    return () => {
      refs.forEach((ref) => ref && observer.unobserve(ref)); // ✅ use same refs
    };
  }, [visibleItems]);

     // Scroll progress line animation
     useEffect(() => {
      const handleScroll = () => {
        if (!timelineRef.current) return;
        const rect = timelineRef.current.getBoundingClientRect();
        const timelineHeight = rect.height;
        
        // Calculate progress to middle of viewport
        const viewportMiddle = window.innerHeight / 2;
        const progress = Math.min(Math.max(viewportMiddle - rect.top, 0), timelineHeight);
  
        // Convert to %
        const progressPercentage = (progress / timelineHeight) * 100;
        setProgressHeight(progressPercentage);
      };
  
      window.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);


  return (
    <section className="bg-cream py-20 md:py-32" ref={timelineRef}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <h2 className="font-playfair text-4xl md:text-6xl font-bold text-center text-espresso mb-4">
          Our Journey
        </h2>
        <p className="text-center text-espresso/70 mb-20 max-w-2xl mx-auto">
          From our first cafe to our latest concept...
        </p>

        <div className="relative">
            {/* Static base timeline line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-warm-taupe/30 hidden md:block transform -translate-x-1/2"></div>

            {/* Progress highlight line with gradient */}
            <div
              className="absolute left-8 md:left-1/2 top-0 w-1 hidden md:block transform -translate-x-1/2 transition-all duration-300 ease-out"
              style={{ 
                height: `${progressHeight}%`,
                background: 'linear-gradient(to bottom, #D4A574, #8B4513)',
                boxShadow: '0 0 20px rgba(212, 165, 116, 0.5)'
              }}
            ></div>


          {restaurants.map((restaurant, index) => (
            <div
              key={index}
              ref={(el) => (itemRefs.current[index] = el)}
              className={`relative mb-16 md:mb-24 transition-all duration-1000 ${
                visibleItems.includes(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <div className={`flex flex-col md:flex-row gap-6 items-start md:items-center ${
                index % 2 === 0 ? 'md:flex-row-reverse' : ''
              }`}>

                {/* Content */}
                <div className={`w-full md:w-5/12 ${
                  index % 2 === 0 ? 'md:text-right md:pr-16' : 'md:pl-16'
                }`}>
                  <span className="font-playfair text-6xl font-bold text-burnt-amber">
                    {restaurant.year}
                  </span>
                  <h3 className="text-espresso font-playfair text-3xl font-bold my-3">
                    {restaurant.name}
                  </h3>
                  <p className="text-espresso/80 mb-6">{restaurant.description}</p>

                  {restaurant.name !== 'Otoño' && <a href={restaurant.instagram}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="bg-burnt-amber text-cream sm:py-3 py-1 sm:px-6 px-2 mb-6  rounded-full inline-flex gap-2 hover:bg-burnt-amber/90 hover:scale-105 transition">
                    Instagram
                  </a>}
                </div>

                {/* Center marker */}
                {/* <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-6 h-6">
                  <div className={`w-6 h-6 rounded-full border-4 transition-all duration-500 ${
                    visibleItems.includes(index)
                      ? 'bg-burnt-amber border-burnt-amber scale-100'
                      : 'bg-warm-taupe border-warm-taupe scale-75'
                  }`}></div>
                </div> */}

                {/* Image */}
                <div className="w-full md:w-5/12">
                  <div className="rounded-2xl shadow-xl overflow-hidden">
                    <img
                      src={restaurant.image}
                      alt={restaurant.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </div>

              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Timeline;
